import axios from "axios";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { styled } from "styled-components";
import RecipeStepImages from "./RecipeStepImages";
import UserAvatar from "./UserAvatar";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  isolation: isolate;
  background: color-mix(
    in srgb,
    ${({ theme }) => theme.colorBackground} 90%,
    transparent
  );
  backdrop-filter: blur(28px) saturate(1.45);
  -webkit-backdrop-filter: blur(28px) saturate(1.45);
  box-shadow: inset 0 1px 0 0
    color-mix(
      in srgb,
      ${({ theme }) => theme.colorOnBackground} 14%,
      transparent
    );
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const RecipeTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colorOnBackground};
`;

const AuthorName = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
`;

const CloseButton = styled.button`
  background: ${({ theme }) => theme.colorSurface};
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colorOnSurface};
  &:hover {
    opacity: 0.8;
  }
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 40px;
  gap: 24px;
  overflow-y: auto;
  max-width: 900px;
  width: 100%;
  align-self: center;
`;

const StepDesc = styled.p`
  color: ${({ theme }) => theme.colorOnBackground};
  font-size: 21px;
  line-height: 1.6;
  text-align: center;
  margin: 0;
  width: 80%;
`;

const ImageGalleryWrapper = styled.div`
  width: 100%;
`;

const BottomBar = styled.div`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colorSurface};
  gap: 12px;
`;

const BottomBarSide = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  &:first-of-type {
    justify-content: flex-start;
  }

  &:last-of-type {
    justify-content: flex-end;
  }
`;

const SideStepTitle = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
`;

const NavButton = styled.button`
  background: ${({ theme }) => theme.colorSurface};
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colorOnSurface};
  flex-shrink: 0;
  &:hover:not(:disabled) {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.25;
    cursor: default;
  }
`;

const CurrentStepInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const CurrentStepTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colorOnBackground};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const StepCounter = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
`;

const RecipeStepLightbox = ({
  steps,
  initialIndex,
  initialImageIndex = 0,
  onClose,
  currentVideo,
  onSeek,
  onPause,
}) => {
  const [index, setIndex] = useState(initialIndex);
  const [channel, setChannel] = useState(null);

  const step = steps[index];

  useEffect(() => {
    if (!currentVideo?.userId) return;
    axios
      .get(
        `${process.env.REACT_APP_RECIPE_API_BASE_URL}/users/find/${currentVideo.userId}`,
        {
          withCredentials: true,
        },
      )
      .then((res) => setChannel(res.data))
      .catch(() => {});
  }, [currentVideo?.userId]);

  useEffect(() => {
    onPause?.();
  }, [onPause]);

  const parseTimeToSeconds = (time) => {
    if (time == null || time === "") return null;
    if (typeof time === "number") return time;
    const parts = String(time).split(":").map(Number);
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  };

  const goTo = useCallback(
    (newIndex) => {
      setIndex(newIndex);
      const seconds = parseTimeToSeconds(steps[newIndex]?.time);
      if (seconds !== null) {
        onSeek?.(seconds);
        onPause?.();
      }
    },
    [steps, onSeek, onPause],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) goTo(index - 1);
      if (e.key === "ArrowRight" && index < steps.length - 1) goTo(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, steps.length, onClose, goTo]);

  const prevStep = steps[index - 1];
  const nextStep = steps[index + 1];

  return ReactDOM.createPortal(
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <TopBar>
        <AuthorInfo>
          <UserAvatar
            src={channel?.img}
            name={channel?.name}
            contrastColor={channel?.contrastColor}
            size={36}
          />
          <AuthorDetails>
            <RecipeTitle>{currentVideo?.title}</RecipeTitle>
            <AuthorName>{channel?.name}</AuthorName>
          </AuthorDetails>
        </AuthorInfo>
        <CloseButton onClick={onClose}>
          <X size={18} />
        </CloseButton>
      </TopBar>

      <Body>
        <StepDesc>{step?.desc}</StepDesc>
        {step?.images?.length > 0 && (
          <ImageGalleryWrapper>
            <RecipeStepImages
              key={index}
              images={step.images}
              imageHeight={360}
              initialImageIndex={index === initialIndex ? initialImageIndex : 0}
            />
          </ImageGalleryWrapper>
        )}
      </Body>

      <BottomBar>
        <BottomBarSide>
          <NavButton onClick={() => goTo(index - 1)} disabled={index === 0}>
            <ChevronLeft size={18} />
          </NavButton>
          <SideStepTitle>{prevStep?.title ?? ""}</SideStepTitle>
        </BottomBarSide>

        <CurrentStepInfo>
          <CurrentStepTitle>{step?.title}</CurrentStepTitle>
          <StepCounter>
            {index + 1} of {steps.length}
          </StepCounter>
        </CurrentStepInfo>

        <BottomBarSide>
          <SideStepTitle>{nextStep?.title ?? ""}</SideStepTitle>
          <NavButton
            onClick={() => goTo(index + 1)}
            disabled={index === steps.length - 1}
          >
            <ChevronRight size={18} />
          </NavButton>
        </BottomBarSide>
      </BottomBar>
    </Overlay>,
    document.body,
  );
};

export default RecipeStepLightbox;
